/**
 * PaymentsApiClient
 * 
 * Handles payment methods, funding sources, and payment processing
 */

import { BaseApiClient } from './BaseApiClient';
import type { PaymentMethod } from '../../types/api/payment';
import { buildPayPalCallbackUrl, paypalApprovalUrl } from '@vaiu/utils';

export class PaymentsApiClient extends BaseApiClient {
  /**
   * Get all payment methods
   */
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await this.get<{ paymentMethods: PaymentMethod[] }>('/payment-methods');
    return response.paymentMethods || [];
  }

  /**
   * Add new payment method
   */
  async addPaymentMethod(method: Omit<PaymentMethod, 'id' | 'createdAt'>): Promise<PaymentMethod> {
    const response = await this.post<{ paymentMethod: PaymentMethod }>('/payment-methods', method);
    return response.paymentMethod;
  }

  /**
   * Remove payment method
   */
  async removePaymentMethod(methodId: string): Promise<void> {
    await this.delete(`/payment-methods/${methodId}`);
  }

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(methodId: string): Promise<void> {
    await this.post(`/payment-methods/${methodId}/default`);
  }

  // Stripe integration
  /**
   * Create Stripe setup intent
   */
  async createStripeSetupIntent(): Promise<{clientSecret: string, customerId: string}> {
    return this.post<{clientSecret: string, customerId: string}>('/payment-methods/stripe/setup-intent');
  }

  /**
   * Confirm Stripe payment method
   */
  async confirmStripePaymentMethod(paymentMethodId: string, cardData: any): Promise<PaymentMethod> {
    const response = await this.post<{ paymentMethod: PaymentMethod }>('/payment-methods/stripe/confirm', { paymentMethodId, cardData });
    return response.paymentMethod;
  }

  /**
   * Charge a card and save it for future use in one step.
   */
  async chargeAndSaveStripeCard(chargeData: {
    amount: number;
    currency: string;
    paymentMethodId: string;
  }): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId: string;
    paymentMethod: {
      id: string;
      type: string;
      last4: string;
      brand: string;
    };
    savedForFutureUse: boolean;
    newBalance: number;
  }> {
    return this.post<{
      success: boolean;
      amount: number;
      currency: string;
      transactionId: string;
      paymentMethod: {
        id: string;
        type: string;
        last4: string;
        brand: string;
      };
      savedForFutureUse: boolean;
      newBalance: number;
    }>('/payment-methods/stripe/charge-and-save', chargeData);
  }

  // Plaid integration
  /**
   * Create Plaid link token
   */
  async createPlaidLinkToken(): Promise<{linkToken: string, expiration: string}> {
    return this.post<{linkToken: string, expiration: string}>('/payment-methods/plaid/link-token');
  }

  /**
   * Exchange Plaid public token
   */
  async exchangePlaidPublicToken(publicToken: string, accountData: any): Promise<PaymentMethod> {
    const response = await this.post<{ paymentMethod: PaymentMethod }>('/payment-methods/plaid/exchange', { publicToken, accountData });
    return response.paymentMethod;
  }

  /**
   * Get Plaid link token (legacy method)
   */
  async getPlaidLinkToken(): Promise<{ linkToken: string }> {
    return this.get<{ linkToken: string }>('/payment-methods/plaid/link-token');
  }

  /**
   * Connect Plaid account
   */
  async connectPlaidAccount(publicToken: string): Promise<PaymentMethod> {
    const result = await this.post<{ paymentMethod: PaymentMethod }>('/payment-methods/plaid/connect', { publicToken });
    return result.paymentMethod;
  }

  // Stripe Financial Connections (Bank Account) integration
  /**
   * Create Stripe Financial Connections session for bank account linking
   */
  async createStripeBankSession(): Promise<{ clientSecret: string }> {
    return this.post<{ clientSecret: string }>('/payment-methods/stripe/bank/session');
  }

  /**
   * Attach Stripe bank account from Financial Connections session.
   * billingName is required by Stripe for us_bank_account - pass the user-confirmed display name.
   */
  async attachStripeBankAccount(financialConnectionsAccountId: string, billingName: string): Promise<{ clientSecret: string }> {
    return this.post<{ clientSecret: string }>('/payment-methods/stripe/bank/attach', {
      financialConnectionsAccountId,
      billingName
    });
  }

  /**
   * Save Stripe bank account payment method after SetupIntent confirmation
   * Backend returns bank info directly: { id, last4, bank_name, institution_name, created_at }
   */
  async saveStripeBankAccount(setupIntentId: string): Promise<PaymentMethod> {
    const response = await this.post<{ paymentMethod?: PaymentMethod; id?: string; last4?: string; bank_name?: string; institution_name?: string; created_at?: string }>('/payment-methods/stripe/bank/save', {
      setupIntentId
    });
    if (response.paymentMethod) {
      return response.paymentMethod;
    }
    const bankInfo = response as { id?: string; last4?: string; bank_name?: string; institution_name?: string; created_at?: string };
    return {
      id: bankInfo.id,
      type: 'plaid',
      isDefault: false,
      details: {
        last4: bankInfo.last4 ?? '****',
        accountName: bankInfo.bank_name ?? bankInfo.institution_name ?? 'Bank Account',
      },
      createdAt: bankInfo.created_at ?? new Date().toISOString(),
    };
  }

  /**
   * Get user's Stripe bank accounts (and Plaid banks when both enabled).
   * Backend returns { banks: { "InstitutionName": [{ id, source, last4, ... }] } } - flatten to PaymentMethod[].
   */
  async getStripeBankAccounts(): Promise<PaymentMethod[]> {
    const response = await this.get<{ banks: Record<string, Array<{ id: string; source?: string; last4?: string; bank_name?: string; institution_name?: string; account_id?: string; created_at?: string }>> }>('/payment-methods/stripe/bank');
    const banksObj = response.banks || {};
    const accounts = Object.values(banksObj).flat();
    return accounts.map((acc) => ({
      id: acc.id,
      type: 'plaid' as const,
      isDefault: false,
      details: {
        last4: acc.last4 ?? '****',
        accountName: acc.bank_name ?? acc.institution_name ?? 'Bank Account',
        accountType: 'checking', // Default account type
        source: acc.source || 'stripe', // Store source to distinguish Stripe vs Plaid banks
      },
      createdAt: acc.created_at || new Date().toISOString(),
    }));
  }

  // PayPal integration
  /**
   * Connect PayPal account
   */
  async connectPayPalAccount(paypalData: any): Promise<PaymentMethod> {
    const response = await this.post<{ paymentMethod: PaymentMethod }>('/payment-methods/paypal/connect', paypalData);
    return response.paymentMethod;
  }

  /**
   * Start PayPal funding (POST /balance/add). Capture is GET /api/v1/paypal on the API.
   */
  async initiatePayPalFunding(fundingData: {
    amount: number;
    currency: string;
    callbackUrl?: string;
    returnUrl?: string;
  }): Promise<{
    approvalURL: string;
    paymentId: string;
  }> {
    const result = await this.topUpWithPayPal(fundingData);
    return {
      approvalURL: paypalApprovalUrl(result),
      paymentId: result.paymentId || '',
    };
  }

  /**
   * @deprecated Capture is performed by the API. Kept as a no-op for old callers.
   */
  async confirmPayPalFunding(paymentId: string): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId: string;
  }> {
    return {
      success: true,
      amount: 0,
      currency: '',
      transactionId: paymentId,
    };
  }

  /**
   * PayPal payout
   */
  async paypalPayout(payoutData: any): Promise<any> {
    return this.post('/transfer-out', {
      ...payoutData,
      method: 'paypal'
    });
  }

  /**
   * Payout to the user's linked Stripe Connect / Express account.
   */
  async stripeConnectPayout(payoutData: {
    amount: number;
    currency: string;
    stripeConnectId: string;
  }): Promise<any> {
    return this.post('/transfer-out', {
      ...payoutData,
      method: 'stripe_connect',
    });
  }

  /**
   * Transfer out to external account
   */
  async transferOut(transferData: any): Promise<any> {
    return this.post('/transfer-out', transferData);
  }

  /**
   * Get transfer out preferences
   */
  async getTransferOutPreferences(): Promise<any> {
    return this.get('/profile/preferences/transfer-out');
  }

  /**
   * Send a one-time verification code to the PayPal email (payout destination).
   */
  async sendPayPalPayoutEmailVerification(email: string): Promise<{
    success: boolean;
    expiresInSeconds?: number;
  }> {
    return this.post('/profile/paypal-email/send-verification', { email });
  }

  /**
   * Confirm PayPal payout email with the code from email.
   */
  async verifyPayPalPayoutEmail(code: string): Promise<{
    success: boolean;
    verified: boolean;
    paypalEmail?: string;
  }> {
    return this.post('/profile/paypal-email/verify', { code });
  }

  /**
   * Process guest payment
   */
  async processGuestPayment(paymentData: any): Promise<any> {
    return this.post('/guest/payment', paymentData);
  }

  /**
   * Notify merchant of guest payment
   */
  async notifyMerchantOfGuestPayment(notificationData: any): Promise<any> {
    return this.post('/merchant/guest-payment-notification', notificationData);
  }

  /**
   * Get acceptable currencies for payment methods
   */
  async getAcceptableCurrencies(): Promise<{
    paypal: string[];
    stripe: string[];
    bank: string[];
    bitcoin: string[];
    all: string[];
  }> {
    // For now, return commonly supported currencies
    // In production, this could be fetched from the server
    return {
      paypal: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
      stripe: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'],
      bank: ['USD', 'EUR', 'GBP', 'CAD'],
      bitcoin: ['BTC', 'USD', 'EUR'], // Bitcoin can handle multiple currencies
      all: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'BTC']
    };
  }

  /**
   * Get transfer history
   */
  async getTransferHistory(filters?: { status?: string; method?: string; limit?: number }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.method) params.append('method', filters.method);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const result = await this.get<{ transfers: any[] }>(`/transfer-history?${params}`);
    return result.transfers;
  }

  /**
   * Top up balance with Stripe
   */
  async topUpWithStripe(topUpData: {
    amount: number;
    currency: string;
    paymentMethodId: string;
  }): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId: string;
    paymentMethod: {
      type: string;
      last4: string;
      brand: string;
    };
  }> {
    return this.post<{
      success: boolean;
      amount: number;
      currency: string;
      transactionId: string;
      paymentMethod: {
        type: string;
        last4: string;
        brand: string;
      };
    }>('/balance/add', {
      ...topUpData,
      source: 'stripe'
    });
  }

  /**
   * Top up balance with ACH
   */
  async topUpWithACH(topUpData: {
    amount: number;
    currency: string;
    paymentMethodId: string;
  }): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId: string;
    paymentMethod: {
      type: string;
      accountName: string;
      last4: string;
    };
  }> {
    return this.post<{
      success: boolean;
      amount: number;
      currency: string;
      transactionId: string;
      paymentMethod: {
        type: string;
        accountName: string;
        last4: string;
      };
    }>('/balance/add', {
      ...topUpData,
      source: 'ach'
    });
  }

  /**
   * Top up balance with PayPal
   */
  async topUpWithPayPal(topUpData: {
    amount: number;
    currency: string;
    paymentMethodId?: string;
    returnUrl?: string;
    cancelUrl?: string;
    context?: string;
    callbackUrl?: string;
    sessionId?: string;
    paypalTab?: boolean;
  }): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId: string;
    approvalURL?: string;
    approvalUrl?: string;
    paymentId?: string;
  }> {
    const {
      returnUrl,
      cancelUrl: _cancelUrl,
      context,
      paymentMethodId,
      callbackUrl: providedCallback,
      sessionId,
      paypalTab,
      ...rest
    } = topUpData;
    const callbackUrl =
      providedCallback ||
      buildPayPalCallbackUrl({
        returnUrl,
        context,
        sessionId,
        paypalTab,
      });

    return this.post<{
      success: boolean;
      amount: number;
      currency: string;
      transactionId: string;
      approvalURL?: string;
      approvalUrl?: string;
      paymentId?: string;
    }>('/balance/add', {
      ...rest,
      source: 'paypal',
      callbackUrl,
      ...(paymentMethodId && paymentMethodId !== 'paypal-funding' ? { paymentMethodId } : {}),
    });
  }

  /**
   * Unified balance add endpoint - handles all payment methods
   */
  async addBalance(data: {
    amount: number;
    currency: string;
    source: 'stripe' | 'paypal' | 'ach' | 'bitcoin' | 'manual' | 'admin';
    paymentMethodId?: string;
    callbackUrl?: string;
    returnUrl?: string;
    cancelUrl?: string;
    details?: any;
    context?: string;
  }): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    transactionId?: string;
    approvalURL?: string;
    paymentId?: string;
    paymentMethod?: {
      type: string;
      last4?: string;
      brand?: string;
      accountName?: string;
    };
    message?: string;
  }> {
    // Add context to return URLs if provided
    let processedData = { ...data };
    
    if (data.context) {
      // Store context in session storage for recovery after payment
      const contextKey = `topup_context_${Date.now()}`;
      sessionStorage.setItem(contextKey, JSON.stringify({
        context: data.context,
        timestamp: Date.now(),
        source: data.source,
        amount: data.amount,
        currency: data.currency
      }));
      
      // Add context key to return URLs
      const addContextToUrl = (url?: string) => {
        if (!url) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}topup_context=${contextKey}`;
      };
      
      processedData.returnUrl = addContextToUrl(data.returnUrl);
      processedData.cancelUrl = addContextToUrl(data.cancelUrl);
      processedData.callbackUrl = addContextToUrl(data.callbackUrl);
    }
    
    return this.post<{
      success: boolean;
      amount: number;
      currency: string;
      transactionId?: string;
      approvalURL?: string;
      paymentId?: string;
      paymentMethod?: {
        type: string;
        last4?: string;
        brand?: string;
        accountName?: string;
      };
      message?: string;
    }>('/balance/add', processedData);
  }

  /**
   * Calculate transfer fees
   */
  async calculateTransferFees(amount: number, currency: string, paymentMethodId?: string): Promise<{
    fees: number;
    netAmount: number;
    estimatedArrival: string;
  }> {
    // Provide default fee calculation since backend endpoint doesn't exist yet
    console.log('⚠️ Using default fee calculation - backend endpoint not implemented');
    
    const fees = amount * 0.025; // 2.5% default fee
    const netAmount = amount - fees;
    
    return {
      fees,
      netAmount,
      estimatedArrival: '1-3 business days'
    };
  }

  /**
   * Get transfer limits
   */
  async getTransferLimits(): Promise<{
    daily: { limit: number; used: number; remaining: number };
    monthly: { limit: number; used: number; remaining: number };
  }> {
    // Provide default limits since backend endpoint doesn't exist yet
    console.log('⚠️ Using default transfer limits - backend endpoint not implemented');
    
    return {
      daily: { limit: 1000, used: 0, remaining: 1000 },
      monthly: { limit: 10000, used: 0, remaining: 10000 }
    };
  }

  // Card-to-Token API methods

  /**
   * Initialize card-to-token payment session
   */
  async initializeCardToToken(request: {
    amount: number;
    currency: string;
  }): Promise<{
    sessionId: string;
    expiresAt: string; // ISO timestamp
  }> {
    return this.post<{
      sessionId: string;
      expiresAt: string;
    }>('/payments/card-to-token/initialize', request);
  }

  /**
   * Process card authorization for card-to-token flow
   */
  async processCardAuthorization(request: {
    sessionId: string;
    cardData: {
      pan: string;
      lastFour: string;
      expiryMonth: number;
      expiryYear: number;
      cardholderName: string;
      cardBrand: string;
      cvv: string;
      encryptedData?: string;
    };
  }): Promise<{
    success: boolean;
    authorizationId?: string;
    authCode?: string; // 6-digit authorization code
    error?: string;
  }> {
    return this.post<{
      success: boolean;
      authorizationId?: string;
      authCode?: string;
      error?: string;
    }>('/payments/card-to-token/authorize', request);
  }

  /**
   * Get card-to-token session status
   */
  async getCardToTokenStatus(sessionId: string): Promise<{
    status: 'pending' | 'authorized' | 'failed' | 'expired';
    authorizationId?: string;
  }> {
    return this.get<{
      status: 'pending' | 'authorized' | 'failed' | 'expired';
      authorizationId?: string;
    }>(`/payments/card-to-token/status/${sessionId}`);
  }
} 
